import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {findDOMNode} from 'react-dom';

import determineIfMobile from '@homeaway/react-determine-if-mobile';

import propTypeDateString from '../utils/propTypeDateString';
import {
    compareDateStrings,
    getDateToString,
    getMonthYear,
} from '../utils/dateUtils';
import isRequiredIf from '../utils/isRequiredIf';
import DatePickerBase from '../DatePickerBase/DatePickerBase';
import DateRangePickerStateless from './DateRangePickerStateless';

import './DateRangePicker.less';

class DateRangePicker extends Component {
    static displayName = 'DateRangePicker';

    static propTypes = {
        /** The location on a page where an action takes place (for edap events) */
        actionLocation: PropTypes.string,
        /** An optional passed in React component, used to render an alert within the date picker pop up. */
        alertTemplateComponent: PropTypes.func,
        /** An optional object, used to pass data to the alert component. */
        alertTemplateData: PropTypes.object, // eslint-disable-line react/forbid-prop-types
        /** Optional labels object to override default locale-driven labels */
        calendarLabels: PropTypes.shape({
            dayNames: PropTypes.arrayOf(PropTypes.string),
            monthNames: PropTypes.arrayOf(PropTypes.string),
            shortDayNames: PropTypes.arrayOf(PropTypes.string),
            shortMonthNames: PropTypes.arrayOf(PropTypes.string),
            /** For locales that need a year label while displaying date e.g. ja-JP*/
            yearLabel: PropTypes.string,
        }),
        /** Centers tooltip position, true by default */
        centered: PropTypes.bool,
        /** The text label displayed on the clear button */
        clearButtonLabel: PropTypes.string,
        /** The text label displayed on the close button */
        closeButtonLabel: PropTypes.string,
        /** Boolean to determine whether the tooltip will close when the user clicks outside of the tooltip. If true, the tooltip will close  and clear immediately on a click outside. If false, the tooltip will not close and clear on a click outside until either a start and end date are selected or date selections are cleared. Default is true. */
        closeOnOutsideClick: PropTypes.bool,
        /** Consolidate check in and check out fields into a single input field */
        consolidateInputs: PropTypes.bool,
        /** The text label displayed on the continue button */
        continueButtonLabel: PropTypes.string,
        /** Options object, eg. {month: 'short', day: 'numeric'} (See https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date/toLocaleDateString) */
        dateFormatOptions: PropTypes.object, // eslint-disable-line react/forbid-prop-types
        /** Object that contains translations for check-in and check-out shown in modal using useDatesInHeader*/
        datesInHeaderLabels: PropTypes.object, // eslint-disable-line react/forbid-prop-types
        /** DayData that will power any passed in template */
        dayData: PropTypes.object, // eslint-disable-line react/forbid-prop-types
        /** An optional passed in React component, used to render each day. */
        dayTemplateComponent: PropTypes.func,
        /** Prevents keyboard input. Useful on iPad to prevent software keyboard from popping up. */
        disableTextInput: PropTypes.bool,
        /** Flag that specifies whether year should be displayed first, useful in locales like ja-JP*/
        displayYearFirst: PropTypes.bool,
        /** Enable date range being the same date. By default, if the end date is selected to be the same as the start date, the entire selection is canceled */
        enableSameDayDateRange: PropTypes.bool,
        /** Selected start date */
        endDate: propTypeDateString,
        /** Text to display in the end date input when an end date is selected. If not specified, the selected end date is displayed. */
        endDateText: PropTypes.string,
        /** Form input error message */
        errorMsg: PropTypes.string,
        /** Flag to determine if there is a fade transition when the tooltip closes. True by default. */
        fadeOut: PropTypes.bool,
        /** FormInput's id, required for a11y */
        id: PropTypes.string.isRequired,
        /** Vertically scrolling calendar that can be embedded on desktop. Eg: In the ChatBot sidebar */
        isInline: PropTypes.bool,
        /** When no date is selected, open the calendar to a specific date. Defaults to today's date. */
        initialDisplayDate: PropTypes.instanceOf(Date),
        /** Add content to the left of the input 1. This can be a string, element, or component */
        inputIcon1: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.element,
            PropTypes.func,
        ]),
        /** Add content to the left of the input 2. This can be a string, element, or component */
        inputIcon2: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.element,
            PropTypes.func,
        ]),
        /** Form input label for the start date input */
        inputLabel1: PropTypes.node,
        /** Form input label for the end date input */
        inputLabel2: PropTypes.node,
        /** Form input name for the start date input*/
        inputName1: isRequiredIf(PropTypes.string, (props) => !props.isInline),
        /** Form input name for the end date input*/
        inputName2: PropTypes.string,
        /** Determines whether the date picker is open or not */
        isOpen: PropTypes.bool,
        /** Object containing aria label translations for the current locale. Required for a11y. */
        i18n: PropTypes.shape({
            /** It is assumed that the parent app will prepare and provide the i18n object matching the shape seen here. */
            ariaLabelDatesCleared: PropTypes.string,
            ariaLabelCheckinCleared: PropTypes.string,
            ariaLabelCheckinSelected: PropTypes.string,
            ariaLabelCheckoutCleared: PropTypes.string,
            ariaLabelCheckoutSelected: PropTypes.string,
            ariaLabelClose: PropTypes.string,
            ariaLabelNextButton: PropTypes.string,
            ariaLabelNotValidCheckin: PropTypes.string,
            ariaLabelNotValidCheckout: PropTypes.string,
            ariaLabelPrevButton: PropTypes.string,
            ariaLabelUnavailable: PropTypes.string,
            /** The aria label used for screen readers to describe how to use the date picker. */
            directions: PropTypes.string,
        }),
        /** A moniker or BCP-47 style language tag string. */
        locale: PropTypes.string,
        /** Maximum allowable date after which the next button will be disabled; scrollable mode stops loading more months. */
        maxDate: PropTypes.instanceOf(Date),
        /** Minimum allowable date before which the prev button will be disabled; scrollable mode uses month and year props. */
        minDate: PropTypes.instanceOf(Date),
        /** There are three different mobile behaviors (2 are only for use with the affiliates program), 1) scrolling - switches to a vertical scrolling list. 2) single-affiliates - special single month mode for affiliates program (fits within 392px height). 3) double-affiliates - special double month mode for affiliates program (fits within 392px height). */
        mobileBehavior: PropTypes.oneOf([
            'scrolling',
            'single-affiliates',
            'double-affiliates',
        ]),
        /* Custom title for modal */
        modalTitle: PropTypes.oneOfType([PropTypes.element, PropTypes.string]),
        /** Optional callback when date picker is closed (X, Esc) */
        onCancel: PropTypes.func,
        /** Optional function to call when the date picker is closed */
        onClose: PropTypes.func,
        /** Function to call when a date is selected on the calendar */
        onDateRangeChange: PropTypes.func,
        /** Function to call when dates are cleared */
        onDateRangeClear: PropTypes.func,
        /** An optional passed in React component, used to trigger Next Available arrival and departure dates when clicked on disabled dates. **/
        onDisabledDayClick: PropTypes.func,
        /** Optional callback for when the date picker gains focus */
        onFocus: PropTypes.func,
        /** Optional callback for when the date picker opens */
        onOpen: PropTypes.func,
        /** Optional callback when date picker toggles between selecting startDate and endDate, passes true as a param if selecting end date */
        onSelectionModeChange: PropTypes.func,
        /** Optional callback when date picker is submitted (continue) */
        onSubmit: PropTypes.func,
        /** The DOM element to use as the parent for the date range picker when visible. This is useful in scenarios where the date range picker needs to break out of its current container due to overflow or z-index issues. */
        parentNode: DatePickerBase.checkElementType,
        /** Defines placement of tooltip. default: 'bottom' */
        placement: PropTypes.oneOf(['top', 'left', 'bottom', 'right']),
        /** Determines which side of the input field inputIcons1 shows. Positions right if true. */
        positionIconRight: PropTypes.bool,
        /** Display-only mode */
        readOnly: PropTypes.bool,
        /** The DOM element that the date range picker needs to scroll with. Only specify this if parentNode was specified and the date picker is displayed in a container that is scrollable independent of the browser window. */
        scrollNode: DatePickerBase.checkElementType,
        /** Controls whether an arrow is displayed between the begin and end date inputs */
        showArrow: PropTypes.bool,
        /** Show "continue" button in modal on mobile */
        showContinueButton: PropTypes.bool,
        /** Show checkin and checkout dates in modal on mobile (replaces modal title) */
        showDatesInHeader: PropTypes.bool,
        /** Controls whether begin and end date inputs are separated visually */
        splitInputs: PropTypes.bool,
        /** Selected start date */
        startDate: propTypeDateString,
        /** Text to display in the start date input when a start date is selected. If not specified, the selected start date is displayed. */
        startDateText: PropTypes.string,
        /** Flag to determine whether to use two Letters for shortDayNames or just one. */
        useConsistentDayOfWeekExperiment: PropTypes.bool,
        /** Flag to determine whether to set hoverDate in alertTemplateData */
        useHoverString: PropTypes.bool,
        /** By default, the input elements are given a data-wdio attribute of "date-range-input-start" and "date-range-input-end".
        If you have more than one on a page you can add a prefix to those two strings by using this property. */
        wdioPrefix: PropTypes.string,
    };

    static defaultProps = {
        ...DateRangePickerStateless.defaultProps,
        onClose: () => {},
        useHoverString: false,
        useConsistentDayOfWeekExperiment: false,
    };

    constructor(props) {
        super(props);

        const {
            startDate,
            isMobile,
            isOpen,
            mobileBehavior,
            initialDisplayDate,
        } = this.props;
        const today = new Date();
        const todayString = getDateToString(today);
        const isMobileScrolling = isMobile && mobileBehavior === 'scrolling';
        let {month, year} = getMonthYear(startDate, initialDisplayDate);

        if (startDate && isMobileScrolling) {
            // If startDate is in the future, we need to reset the month & year to the current date.
            if (compareDateStrings(todayString, startDate) === 1) {
                month = today.getMonth();
                year = today.getFullYear();
            }
        }

        this.state = {
            alertTemplateData: this.props.alertTemplateData,
            month,
            year,
            isClosing: false,
            isDelayed: false,
            isOpen,
            selectionChangeMode: 'all',
        };
    }

    static getDerivedStateFromProps(nextProps) {
        const {useHoverString, alertTemplateData} = nextProps;
        if (!useHoverString) {
            return {
                alertTemplateData,
            };
        }

        return null;
    }

    componentDidUpdate(prevProps, prevState) {
        const {isMobile, isOpen} = this.props;

        if (prevProps.isOpen !== isOpen && prevState.isOpen !== isOpen) {
            this.setState({isOpen});
        }
        // For mobile, we render a Modal with a NavCalendar within the Modal.
        // However, the Modal tries to find all focusable elements within itself
        // before NavCalendar is loaded, so it does not find the first focusable day
        // in the calendar. We toggle state.isDelayed after this component updates to
        // trigger componentDidUpate() within Modal which will find all focusable elements
        // including the NavCalendar. This ensures the Modal content is tab accessible.
        // (We use a timeout to display an empty modal first, then start rendering the calendar.)
        if (isMobile && this.state.isDelayed) {
            window.setTimeout(() => {
                this.setState({isDelayed: false});
            }, 50);
        }
    }

    updateSelectionChangeMode = (mode) => {
        this.setState({selectionChangeMode: mode});
        this.props.onSelectionModeChange(mode === 'end');
    };

    // eslint-disable-next-line complexity
    handleOpen = (selectionChangeMode, formControlClass) => {
        const {
            disableTextInput,
            isMobile,
            startDate,
            mobileBehavior,
            initialDisplayDate,
        } = this.props;
        const {month, year} = getMonthYear(startDate, initialDisplayDate);

        // only do this if mobile and scrolling, which used to be the
        // only supported mobile mode, but isn't anymore
        if (isMobile && mobileBehavior === 'scrolling') {
            // Open, but set isDelayed to true so the calendar waits a moment to render.
            this.setState({
                isOpen: true,
                isDelayed: true,
            });

            if (startDate) {
                const today = new Date();
                const todayString = getDateToString(today);

                // If startDate is in the future, we need to reset the month & year to the current date.
                if (compareDateStrings(todayString, startDate) === 1) {
                    this.setState({
                        month: today.getMonth(),
                        year: today.getFullYear(),
                    });
                }
            }
        } else if (document) {
            const dateInput = findDOMNode(this).getElementsByClassName(
                formControlClass
            )[0];
            // prevent double setState when dateInput receives focus
            // as we may be clicking on the overlay, rather than tabbing to the input
            // force focus of the input for text entry (text entry is TODO)
            if (!disableTextInput && document.activeElement !== dateInput) {
                dateInput.focus();
            }

            this.setState({
                isOpen: true,
                month,
                year,
            });
        }

        if (this.state.selectionChangeMode !== selectionChangeMode) {
            this.updateSelectionChangeMode(selectionChangeMode);
        }
    };

    handleClose = (callback) => {
        if (this.state.isOpen) {
            this.setState(
                {
                    isOpen: false,
                    isClosing: true,
                    selectionChangeMode: 'all',
                },
                () => {
                    this.props.onClose();

                    if (callback) {
                        callback();
                    }
                }
            );
        }
    };

    handleFinishClose = () => {
        this.setState({
            isClosing: false,
        });
    };

    onHoverDay = (date) => {
        if (this.props.useHoverString) {
            this.setState({alertTemplateData: {endDate: date}});
        }
    };

    render() {
        return (
            // props must come first so we can override any shared default props
            <DateRangePickerStateless
                {...this.props}
                alertTemplateData={this.state.alertTemplateData}
                closeCalendar={this.handleClose}
                isClosing={this.state.isClosing}
                isDelayed={this.state.isDelayed}
                isOpen={this.state.isOpen}
                month={this.state.month}
                onFinishClose={this.handleFinishClose}
                onHoverDay={this.onHoverDay}
                openCalendar={this.handleOpen}
                selectionChangeMode={this.state.selectionChangeMode}
                updateSelectionChangeMode={this.updateSelectionChangeMode}
                year={this.state.year}
            />
        );
    }
}

export default determineIfMobile(DateRangePicker);
